import { db } from '../firebaseConfig';
import { collection, addDoc, doc, getDoc, updateDoc, deleteDoc, deleteField, query, where, getDocs } from 'firebase/firestore';

/**
 * Service to handle exercise-library persistence.
 *
 * The library is two-tier:
 *   - Official/curated docs carry the legacy literal userId "global"
 *     (seeded/console-managed — no longer created through the app).
 *   - New custom exercises are owner-scoped: they carry ownerUserId (the
 *     Manager's uid, matching the routines/logs/profiles convention), making
 *     them private to that account and shared across all its Heroes. No
 *     userId field is written on new docs — that field is legacy/global-only.
 *
 * targetMuscle (the muscle group) is mandatory; mainMuscle (the specific
 * sub-muscle) is optional as of 2026-08-21, per direct user request —
 * omitted from the write entirely when blank rather than written as an
 * empty string, matching firestore.rules' relaxed validation (absent OR a
 * non-empty string, never ""). Exercises with no mainMuscle fall back to the
 * app's existing "—" display convention wherever it's shown, same as the
 * pre-2026-08-12 exercises that predate the field.
 */
export const createExercise = async (name, targetMuscle, mainMuscle, ownerUserId) => {
  try {
    const docData = { name, targetMuscle, ownerUserId };
    if (mainMuscle) docData.mainMuscle = mainMuscle;
    const docRef = await addDoc(collection(db, "exercises"), docData);

    console.log("Exercise Recruited: ", docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};

/**
 * Update an existing custom exercise's name/targetMuscle/mainMuscle. Only
 * ever called against this account's own custom docs — the update rule
 * blocks editing official/global docs (they have no ownerUserId field to
 * match against). An empty mainMuscle clears any previously-set value via
 * deleteField() (rather than writing ""), so editing an exercise down to
 * "no main muscle" actually removes the field instead of silently failing
 * firestore.rules' non-empty-string validation.
 */
export const updateExercise = async (exerciseId, name, targetMuscle, mainMuscle) => {
  try {
    await updateDoc(doc(db, "exercises", exerciseId), {
      name,
      targetMuscle,
      mainMuscle: mainMuscle || deleteField()
    });
    console.log("Exercise Updated: ", exerciseId);
    return { success: true, id: exerciseId };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};

/**
 * Delete a custom exercise outright (must be owned by ownerUserId — same
 * ownership guard as merge, verified via getDoc before deleting). Unlike
 * mergeExercise, this does NOT repoint any logs/routines that reference it
 * first — any existing logged set or routine entry pointing at this
 * exerciseId is left as-is and falls back to the app's existing
 * "(deleted exercise)" display convention (already handled by
 * resolveExerciseName in RoutinePicker.jsx/WorkoutLog.jsx). Firestore's
 * owner-scoped `exercises` delete rule (added for account deletion) already
 * covers this without any rules change, same as routinesService.deleteRoutine.
 */
export const deleteExercise = async (exerciseId, ownerUserId) => {
  try {
    const snap = await getDoc(doc(db, "exercises", exerciseId));
    if (!snap.exists() || snap.data().ownerUserId !== ownerUserId) {
      return { success: false, error: "You can only delete exercises you created." };
    }
    await deleteDoc(doc(db, "exercises", exerciseId));
    console.log("Exercise Deleted: ", exerciseId);
    return { success: true };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};

/**
 * Merge a custom exercise (sourceExerciseId, must be owned by ownerUserId)
 * into another exercise already in the library (targetExerciseId — official
 * or another custom doc). Repoints every logged set and every routine entry
 * that references the source over to the target, then deletes the
 * now-redundant source doc. E.g. a user creates "RDL", later notices the
 * official "Romanian Deadlift" already covers it, and wants to fold their
 * history into the canonical exercise instead of having two entries split
 * their progress tracking.
 *
 * Verifies ownership via a getDoc up front (belt-and-suspenders on top of
 * the UI only ever offering this on the account's own custom rows) so a
 * caller can never leave logs/routines repointed and then fail on the final
 * delete because the source wasn't actually theirs to remove.
 *
 * Sequential getDocs + Promise.all(updateDoc) per collection, same
 * established pattern as accountService.deleteAccount, rather than a
 * writeBatch — this app has no precedent for batched writes and a handful
 * of sequential awaits is easy to reason about for what's normally a small
 * number of affected docs. Logs/routines are repointed BEFORE the source
 * exercise doc is deleted, so a failure partway through never leaves the
 * source doc gone while something still references it.
 */
export const mergeExercise = async (sourceExerciseId, targetExerciseId, ownerUserId) => {
  if (sourceExerciseId === targetExerciseId) {
    return { success: false, error: "Can't merge an exercise into itself." };
  }
  try {
    const sourceSnap = await getDoc(doc(db, "exercises", sourceExerciseId));
    if (!sourceSnap.exists() || sourceSnap.data().ownerUserId !== ownerUserId) {
      return { success: false, error: "You can only merge exercises you created." };
    }

    // 1. Repoint every logged set.
    const logsSnap = await getDocs(query(
      collection(db, "logs"),
      where("ownerUserId", "==", ownerUserId),
      where("exerciseId", "==", sourceExerciseId)
    ));
    await Promise.all(logsSnap.docs.map((d) => updateDoc(d.ref, { exerciseId: targetExerciseId })));

    // 2. Repoint every routine that references the source exercise. Firestore
    // can't query "array of objects contains a field == X", so this fetches
    // all of this account's routines (a plain owner-scoped equality query,
    // same as RoutinePicker's own listener) and filters/rewrites client-side.
    const routinesSnap = await getDocs(query(
      collection(db, "routines"),
      where("ownerUserId", "==", ownerUserId)
    ));
    const affectedRoutines = routinesSnap.docs.filter((d) =>
      (d.data().exercises || []).some((item) => item.exerciseId === sourceExerciseId)
    );
    await Promise.all(affectedRoutines.map((d) => {
      const rewritten = (d.data().exercises || []).map((item) =>
        item.exerciseId === sourceExerciseId ? { ...item, exerciseId: targetExerciseId } : item
      );
      // If the routine already had the target exercise too, the merge would
      // otherwise leave two entries for the same exercise in one routine —
      // drop the later duplicate, keep the earlier occurrence's order/sets.
      const seen = new Set();
      const deduped = rewritten.filter((item) => {
        if (seen.has(item.exerciseId)) return false;
        seen.add(item.exerciseId);
        return true;
      });
      return updateDoc(d.ref, { exercises: deduped });
    }));

    // 3. The source exercise is now unreferenced — delete it.
    await deleteDoc(doc(db, "exercises", sourceExerciseId));

    console.log("Exercise Merged: ", sourceExerciseId, "->", targetExerciseId);
    return { success: true, logsUpdated: logsSnap.docs.length, routinesUpdated: affectedRoutines.length };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};
